/* eslint-disable id-blacklist */
/* eslint-disable max-len */
import { MessagingService } from './messaging.service';
import { NetworkService } from './network.service';
import { BehaviorSubject, Observable, catchError, of } from 'rxjs';
import { OfflineService } from './offline.service';
import { Injectable, OnInit } from '@angular/core';
import { UtilitiesService } from './utilities.service';
import { RestService } from './rest.service';
import { Request } from './../model/request';
import { TaskFilterService } from './task-filter.service';

@Injectable({
  providedIn: 'root',
})
export class DataProviderService {
  private jobsPlan: any[] = [];
  private eljakimJobsPlan: any[] = [];
  private nonVisitPlan: any[] = [];
  private visitResults: any[] = [];
  private pendingRequests: Request[] = [];
  private intervalId;

  constructor(
    private offlineservice: OfflineService,
    private apiservice: RestService,
    private networkservice: NetworkService,
    private messagingservice: MessagingService,
    private utilities: UtilitiesService,
    private taskFilterService: TaskFilterService
  ) {
    this.subscribeForNetworkChanges();
  }

  getUserName() {
    return this.apiservice.getUsername();
  }

  getDBUserName() {
    return this.apiservice.getDBUserName();
  }

  getJob(id: number) {
    return new Observable<any>((observer) => {
      this.jobsPlan.forEach((job) => {
        if (job.id === id) {
          observer.next(job);
        }
      });
    });
  }

  getEljakimJob(visitId: string, fetchFromApiIfNotFound: boolean = true) {
    return new Observable<any>((observer) => {
      const visit = this.eljakimJobsPlan.find(
        (job) => job.visitId.toString() === visitId
      );

      if (visit) {
        observer.next(visit);
        observer.complete();
      } else if (fetchFromApiIfNotFound && this.networkservice.isOnline()) {
        const email = this.apiservice.emailSubject.value;
        const lastWeek = this.taskFilterService.getLastWeek();

        this.getOnlineEljakimVisitsPlan(new Date(), email, lastWeek)
          .then((allVisits) => {
            this.eljakimJobsPlan = allVisits;

            const refreshedVisit = allVisits.find(
              (job: any) => job.visitId.toString() === visitId
            );

            if (refreshedVisit) {
              observer.next(refreshedVisit);
            } else {
              observer.next(undefined);
            }
            observer.complete();
          })
          .catch((error) => {
            observer.next(undefined);
            observer.complete();
          });
      } else {
        observer.next(undefined);
        observer.complete();
      }
    });
  }

  getEljakimJobs(date: string) {
    return new Observable<any>((observer) => {
      const jobs = this.eljakimJobsPlan.filter(
        (job) => job.planningDate.toString() === date
      );
      observer.next(jobs);
    });
  }

  getJobsPlan(date: Date, refreshFromApi: boolean = true): Observable<any> {
    return new Observable<any>((observer) => {
      if (refreshFromApi) {
        if (this.networkservice.isOnline()) {
          this.getOnlineVisitsPlan(date).then((data) => {
            observer.next(data);

            this.jobsPlan = data;
            observer.complete();
          });
        } else {
          if (this.jobsPlan) {
            observer.next(this.jobsPlan);
          }
          this.getOfflineVisitsPlan(date).then((data) => {
            observer.next(JSON.parse(data));
            this.jobsPlan = JSON.parse(data);
            observer.complete();
          });
        }
      } else {
        if (this.jobsPlan) {
          observer.next(this.jobsPlan);
        }
        this.getOfflineVisitsPlan(date).then((data) => {
          observer.next(data);
          this.jobsPlan = data;

          observer.complete();
        });
      }
    }).pipe(catchError((error) => of(error)));
  }

  getVisitResults() {
    return new Observable<any>((observer) => {
      if (this.networkservice.isOnline()) {
        this.getOnlineVisitResults().then((data) => {
          observer.next(data);

          this.visitResults = data;
          observer.complete();
        });
      } else {
        if (this.visitResults) {
          observer.next(this.visitResults);
        }
        this.getOfflineVisitResults().then((data) => {
          observer.next(JSON.parse(data));
          this.visitResults = JSON.parse(data);
          observer.complete();
        });
      }
    });
  }

  getEljakimJobsPlan(
    email: string,
    date: Date,
    refreshFromApi: boolean = true,
    lastWeek: boolean = false
  ): Observable<any> {
    return new Observable<any>((observer) => {
      if (refreshFromApi) {
        if (this.networkservice.isOnline()) {
          this.getOnlineEljakimVisitsPlan(date, email, lastWeek).then(
            (data) => {
              observer.next(data);

              this.eljakimJobsPlan = data;
              observer.complete();
            }
          );
        } else {
          if (this.eljakimJobsPlan) {
            observer.next(this.eljakimJobsPlan);
          }
          this.getOfflineEljakimVisitsPlan(date).then((data) => {
            observer.next(JSON.parse(data));
            this.eljakimJobsPlan = JSON.parse(data);
            observer.complete();
          });
        }
      } else {
        if (this.eljakimJobsPlan) {
          observer.next(this.eljakimJobsPlan);
        }
        this.getOfflineEljakimVisitsPlan(date).then((data) => {
          observer.next(data);
          this.eljakimJobsPlan = data;

          observer.complete();
        });
      }
    }).pipe(catchError((error) => of(error)));
  }

  getNonVisitsPlan(
    email: string,
    date: Date,
    refreshFromApi: boolean = true,
    lastWeek: boolean = false
  ): Observable<any> {
    return new Observable<any>((observer) => {
      if (refreshFromApi) {
        if (this.networkservice.isOnline()) {
          this.getOnlineNonVisitsPlan(date, email, lastWeek).then((data) => {
            observer.next(data);

            this.nonVisitPlan = data;
            observer.complete();
          });
        } else {
          if (this.nonVisitPlan) {
            observer.next(this.nonVisitPlan);
          }
          this.getOfflineNonVisitsPlan(date).then((data) => {
            observer.next(JSON.parse(data));
            this.nonVisitPlan = JSON.parse(data);
            observer.complete();
          });
        }
      } else {
        if (this.nonVisitPlan) {
          observer.next(this.nonVisitPlan);
        }
        this.getOfflineNonVisitsPlan(date).then((data) => {
          observer.next(data);
          this.nonVisitPlan = data;

          observer.complete();
        });
      }
    }).pipe(catchError((error) => of(error)));
  }

  getQuestionnaire(
    visitId: number,
    parentObject: string,
    customFormId: number,
    refreshFromApi: boolean = true
  ): Observable<any> {
    return new Observable<any>((observer) => {
      if (refreshFromApi) {
        if (this.networkservice.isOnline()) {
          this.getOnlineQuestionnaire(visitId, parentObject, customFormId).then(
            (data) => {
              observer.next(data);
              observer.complete();
            }
          );
        } else {
          this.getOfflineQuestionnaire(
            visitId,
            parentObject,
            customFormId
          ).then((data) => {
            if (data !== null) {
              observer.next(JSON.parse(data));
            } else {
              observer.next([]);
            }
            observer.complete();
          });
        }
      } else {
        this.getOfflineQuestionnaire(visitId, parentObject, customFormId).then(
          (data) => {
            observer.next(data);

            observer.complete();
          }
        );
      }
    });
  }

  getPhotos(answerId: number): Observable<any> {
    return new Observable<any>((observer) => {
      if (this.networkservice.isOnline()) {
        this.getOnlinePhotos(answerId).then((data) => {
          observer.next(data);
          observer.complete();
        });
      } else {
        observer.next(0);
        observer.complete();
      }
    });
  }

  getComments(answerId: number): Observable<any> {
    return new Observable<any>((observer) => {
      if (this.networkservice.isOnline()) {
        this.getOnlineComments(answerId).then((data) => {
          observer.next(data);
          observer.complete();
        });
      } else {
        observer.next(0);
        observer.complete();
      }
    });
  }

  postAnswer(answerId: number, json): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      if (this.networkservice.isOnline()) {
        this.apiservice
          .saveAnswers(answerId, json)
          .then((data) => {
            resolve(data);
          })
          .catch((error) => reject(error));
      } else {
        // storing pendingRequest for future send
        const request: Request = this.apiservice.getSaveAnswerOfflineRequest(
          answerId,
          json
        );
        this.pendingRequests.push(request);
        this.storePendingRequests();
      }
    });
  }

  saveVisitData(visitId: number, json): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      if (this.networkservice.isOnline()) {
        this.apiservice
          .saveVisitData(visitId, json)
          .then((data) => {
            resolve(data);
          })
          .catch((error) => reject(error));
      } else {
        // storing pendingRequest for future send
        const request: Request = this.apiservice.getSaveVisitDataOfflineRequest(
          visitId,
          json
        );
        this.pendingRequests.push(request);
        this.storePendingRequests();
      }
    });
  }

  savePhoto(answerId: number, json): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      if (this.networkservice.isOnline()) {
        this.apiservice
          .savePhoto(answerId, json)
          .then((data) => {
            resolve(data);
          })
          .catch((error) => reject(error));
      } else {
        // storing pendingRequest for future send
        const request: Request = this.apiservice.getSavePhotoOfflineRequest(
          answerId,
          json
        );
        this.pendingRequests.push(request);
        this.storePendingRequests();
        resolve(json);
      }
    });
  }

  updatePhoto(photoId: number, json): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      if (this.networkservice.isOnline()) {
        this.apiservice
          .updatePhoto(photoId, json)
          .then((data) => {
            resolve(data);
          })
          .catch((error) => reject(error));
      } else {
        // storing pendingRequest for future send
        const request: Request = this.apiservice.getUpdatePhotoOfflineRequest(
          photoId,
          json
        );
        this.pendingRequests.push(request);
        this.storePendingRequests();
        resolve(json);
      }
    });
  }

  saveComment(answerId: number, json): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      if (this.networkservice.isOnline()) {
        this.apiservice
          .saveComment(answerId, json)
          .then((data) => {
            resolve(data);
          })
          .catch((error) => reject(error));
      } else {
        // storing pendingRequest for future send
        const request: Request = this.apiservice.getSaveCommentOfflineRequest(
          answerId,
          json
        );
        this.pendingRequests.push(request);
        this.storePendingRequests();
        resolve(json);
      }
    });
  }

  updateComment(commentId: number, json): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      if (this.networkservice.isOnline()) {
        this.apiservice
          .updateComment(commentId, json)
          .then((data) => {
            resolve(data);
          })
          .catch((error) => reject(error));
      } else {
        // storing pendingRequest for future send
        const request: Request = this.apiservice.getUpdateCommentOfflineRequest(
          commentId,
          json
        );
        this.pendingRequests.push(request);
        this.storePendingRequests();
        resolve(json);
      }
    });
  }

  deletePhoto(photoId: number): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      if (this.networkservice.isOnline()) {
        this.apiservice
          .deletePhoto(photoId)
          .then((data) => {
            resolve(data);
          })
          .catch((error) => reject(error));
      } else {
        // storing pendingRequest for future send
        const request: Request =
          this.apiservice.getDeletePhotoOfflineRequest(photoId);
        this.pendingRequests.push(request);
        this.storePendingRequests();
      }
    });
  }

  deleteComment(commentId: number): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      if (this.networkservice.isOnline()) {
        this.apiservice
          .deleteComment(commentId)
          .then((data) => {
            resolve(data);
          })
          .catch((error) => reject(error));
      } else {
        // storing pendingRequest for future send
        const request: Request =
          this.apiservice.getDeleteCommentOfflineRequest(commentId);
        this.pendingRequests.push(request);
        this.storePendingRequests();
      }
    });
  }

  saveOfflineQuestionnaire(
    questionnaire: any,
    parentObject: string,
    id: number
  ) {
    this.offlineservice.store(
      this.apiservice.getUsername(),
      'questionnaire/' + parentObject + '/' + id,
      JSON.stringify(questionnaire)
    );
  }

  getPendingRequests(): Promise<Request[]> {
    return new Promise<Request[]>((resolve, reject) => {
      this.offlineservice
        .get(this.getUserName(), 'requests')
        .then((dataRaw) => {
          let data: Request[] = JSON.parse(dataRaw);
          if (data == null) {
            data = [];
          }
          this.pendingRequests = data;
          resolve(data);
        })
        .catch((error) => {
          const data: Request[] = [];
          this.pendingRequests = data;
          resolve(data);
        });
    });
  }

  storePendingRequests() {
    this.messagingservice.pendingRequestsChanged.emit(
      this.pendingRequests.length
    );
    return this.offlineservice.store(
      this.getUserName(),
      'requests',
      JSON.stringify(this.pendingRequests)
    );
  }

  wait(ms) {
    const start = new Date().getTime();
    let end = start;
    while (end < start + ms) {
      end = new Date().getTime();
    }
  }

  sendAllPendingRequests(): Promise<boolean> {
    return new Promise<boolean>(async (resolve, reject) => {
      this.messagingservice.pendingRequestsSending.emit(
        this.pendingRequests.length
      );
      const pending: Request[] = [];
      for (const request of this.pendingRequests) {
        if (await this.apiservice.putPendingRequest(request)) {
        } else {
          pending.push(request);
        }
      }
      this.pendingRequests = pending;
      this.messagingservice.pendingRequestsSent.emit();
      this.storePendingRequests();
      if (this.pendingRequests.length > 0) {
        resolve(false);
      } else {
        resolve(true);
      }
    });
  }

  subscribeForNetworkChanges() {
    this.messagingservice.networkStatusChanged.subscribe((status: boolean) => {
      if (status) {
        this.sendAllPendingRequests();
      }
    });
  }

  startDataSychronization(date: Date) {
    this.intervalId = setInterval(() => this.synchronizeData(date), 60000);
  }

  synchronizeData(date: Date) {
    const text: string = date.toLocaleTimeString();
    this.getJobsPlan(date, true)
      .toPromise()
      .then((data) => {
        this.messagingservice.dataSynchronized.emit();
      });
  }

  stopDataSynchronization() {
    clearInterval(this.intervalId);
  }

  clearLocalStorage() {
    this.offlineservice.clearLocalStorage();
  }

  private getOfflineVisitsPlan(date: Date): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      let jobsPlan: any = [];
      const dateTextToday: string = this.utilities.formatDatetime(
        date,
        'yyyy-mm-dd'
      );

      this.offlineservice
        .getVisitsPlan(dateTextToday)
        .then((data) => {
          jobsPlan = JSON.parse(data);
          resolve(jobsPlan);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  private getOfflineEljakimVisitsPlan(date: Date): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      let jobsPlan: any = [];
      const dateTextToday: string = this.utilities.formatDatetime(
        date,
        'yyyy-mm-dd'
      );

      this.offlineservice
        .getEljakimVisitsPlan(dateTextToday)
        .then((data) => {
          jobsPlan = JSON.parse(data);
          resolve(jobsPlan);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  private getOfflineNonVisitsPlan(date: Date): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      let jobsPlan: any = [];
      const dateTextToday: string = this.utilities.formatDatetime(
        date,
        'yyyy-mm-dd'
      );

      this.offlineservice
        .getNonVisitsPlan(dateTextToday)
        .then((data) => {
          jobsPlan = JSON.parse(data);
          resolve(jobsPlan);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  private getOnlineVisitsPlan(date: Date): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      let jobsPlan: any = [];
      let plan: any;

      const dateTextToday: string = this.utilities.formatDatetime(
        date,
        'yyyy-mm-dd'
      );
      const visits = this.apiservice.getVisitsPlan().then((data) => {
        plan = data;
      });
      Promise.resolve(visits)
        .then(() => {
          jobsPlan = plan;
          this.offlineservice.store(
            this.apiservice.getUsername(),
            'schedule/' + dateTextToday,
            JSON.stringify(plan)
          );
          resolve(jobsPlan);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  private getOnlineEljakimVisitsPlan(
    date: Date,
    email: string,
    lastWeek: boolean = false
  ): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      let jobsPlan: any = [];
      let plan: any;

      const dateTextToday: string = this.utilities.formatDatetime(
        date,
        'yyyy-mm-dd'
      );
      const visits = this.apiservice
        .getEljakimVisitsPlan(email, lastWeek)
        .then((data) => {
          plan = data;
        });
      Promise.resolve(visits)
        .then(() => {
          jobsPlan = plan;
          this.offlineservice.store(
            this.apiservice.getUsername(),
            'eljakim_schedule/' + dateTextToday,
            JSON.stringify(plan)
          );
          resolve(jobsPlan);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  private getOnlineNonVisitsPlan(
    date: Date,
    email: string,
    lastWeek: boolean = false
  ): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      let jobsPlan: any = [];
      let plan: any;

      const dateTextToday: string = this.utilities.formatDatetime(
        date,
        'yyyy-mm-dd'
      );
      const visits = this.apiservice
        .getNonVisitsPlan(email, lastWeek)
        .then((data) => {
          plan = data;
        });
      Promise.resolve(visits)
        .then(() => {
          jobsPlan = plan;
          this.offlineservice.store(
            this.apiservice.getUsername(),
            'non_visits_schedule/' + dateTextToday,
            JSON.stringify(plan)
          );
          resolve(jobsPlan);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  private getOnlineVisitResults(): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      let visitResults: any = [];
      const visits = this.apiservice.getVisitResults().then((data) => {
        visitResults = data;
      });
      Promise.resolve(visits)
        .then(() => {
          this.offlineservice.store(
            this.apiservice.getUsername(),
            'visitsResults',
            JSON.stringify(visitResults)
          );
          resolve(visitResults);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  private getOfflineVisitResults(): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      let visitResults: any = [];

      this.offlineservice
        .getVisitResults()
        .then((data) => {
          visitResults = JSON.parse(data);
          resolve(visitResults);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  private getOfflineQuestionnaire(
    id: number,
    parentObject: string,
    customFormId: number
  ): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      let questionnaire: any = [];

      this.offlineservice
        .getQuestionnaire(id, parentObject, customFormId)
        .then((data) => {
          questionnaire = JSON.parse(data);

          resolve(questionnaire);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  private getOnlineQuestionnaire(
    id: number,
    parentObject: string,
    customFormId: number
  ): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      let questionnaire = [];

      const visits = this.apiservice
        .getQuestionnaire(id, parentObject, customFormId)
        .then((data) => {
          questionnaire = data;
        });
      Promise.resolve(visits)
        .then(() => {
          this.offlineservice.store(
            this.apiservice.getUsername(),
            'questionnaire/' + customFormId + '/' + parentObject + '/' + id,
            JSON.stringify(questionnaire)
          );
          resolve(questionnaire);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  private getOnlinePhotos(answerId: number): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      let photos = [];

      const photosPromise = this.apiservice.getPhotos(answerId).then((data) => {
        photos = data;
      });
      Promise.resolve(photosPromise)
        .then(() => {
          resolve(photos);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  private getOnlineComments(answerId: number): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      let comments = [];

      const commentsPromise = this.apiservice
        .getComments(answerId)
        .then((data) => {
          comments = data;
        });
      Promise.resolve(commentsPromise)
        .then(() => {
          resolve(comments);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
}
